import { useEffect, useMemo, useState } from 'react'
import * as safe from '../../lib/safeStorage'
import { Api } from '../../lib/apiClient'

type Prefs = {
  diet: string[]
  cuisine: string[]
  entertainment: string[]
  activities: string[]
  shareWithPartner: boolean
  zip: string
  searchDistance: number
  starRating: number
  costLevel: number
  preferredTimes: string[]
}
type ArrayPrefKey = 'diet' | 'cuisine' | 'entertainment' | 'activities' | 'preferredTimes'

const DIET_OPTIONS = [
  'Vegetarian','Vegan','Pescatarian','Gluten-Free','Dairy-Free','Keto','Paleo','Halal','Kosher','None'
]
const CUISINE_OPTIONS = [
  'Italian','Thai','Japanese','Chinese','Indian','Mexican','Mediterranean','American','French','Korean','Vietnamese','Spanish','Greek','Middle Eastern','BBQ','Sushi','Burgers','Pizza','Seafood','Ethiopian','Brazilian','Turkish','Caribbean','Lebanese','Peruvian','African','German'
]
const ENTERTAINMENT_OPTIONS = [
  'Live Music','Movies','Comedy','Theater','Museums','Art Galleries','Dancing','Wine Tasting','Coffee','Bookstore','Escape Room','Karaoke','Arcade','Bowling','Mini Golf','Go Karts','Trampoline Park','Magic Shows','Puppet Shows','Storytelling','Poetry Readings','Book Clubs','Writing Groups','Language Exchange','Cultural Events','International Festivals','Holiday Events','Circus','Carnivals','Fairs','Festivals','Conferences','Workshops','Seminars','Meetups','Networking Events','Charity Events','Volunteering','Community Service','Fundraising','Auction Houses','Art Auctions','Car Shows','Air Shows','Boat Shows','Home Shows','Garden Shows','Flower Shows','Pet Shows','Dog Shows','Cat Shows','Reptile Shows','Aquarium Visits','Zoo Visits','Wildlife Sanctuaries','Nature Centers','Planetariums','Observatories','Science Centers','Children\'s Museums','History Museums','Art Museums','Natural History Museums','Aviation Museums','Maritime Museums','Military Museums','Technology Museums','Space Museums','Music Museums','Sports Museums','Automotive Museums','Railroad Museums','Trampoline Parks','Bounce Houses','Inflatable Parks','Water Parks','Amusement Parks','Theme Parks'
]

const ACTIVITIES_OPTIONS = [
  'Painting','Glass Blowing','Swimming','Arts and Crafts','Pottery','Archery','Dog Walking','Cats','Photography','Rock Climbing','Kayaking','Surfing','Skiing','Snowboarding','Ice Skating','Tennis','Basketball','Soccer','Volleyball','Baseball','Golf','Fishing','Camping','Gardening','Yoga','Pilates','Martial Arts','Boxing','Dance Classes','Music Lessons','Language Classes','Chess','Board Games','Video Games','Virtual Reality','Laser Tag','Paintball','Airsoft','Skydiving','Bungee Jumping','Hot Air Balloon','Horseback Riding','Cycling','Running','Marathon','Triathlon','Water Polo','Scuba Diving','Snorkeling','Sailing','Boating','Jet Skiing','Parasailing','Zip Lining','Hiking','Mountain Biking','Trail Running','Nature Walks','Bird Watching','Stargazing','Astronomy','Geocaching','Urban Exploration','Food Tours','Wine Tours','Brewery Tours','Distillery Tours','Farm Tours','Factory Tours','Historical Tours','Ghost Tours','Architecture Tours','Art Walks','Street Art','Graffiti','Mural Tours','Flea Markets','Antique Shops','Vintage Stores','Thrift Shopping','Artisan Markets','Craft Fairs','Picnic','Beach Walk','Cooking Class','Seasonal Activities','Spring Activities','Summer Activities','Fall Activities','Winter Activities'
]

const TIME_OPTIONS = [
  'Today','Tomorrow','This Weekend','Next Week','Morning','Afternoon','Evening','Night','Weekdays','Weekends','Any Time'
]

export default function PreferencesSummaryCard() {
  const [isEditing, setIsEditing] = useState(false)
  const [prefs, setPrefs] = useState<Prefs>(() => {
    // seed from localStorage or defaults
    const raw = safe.getItem('ds_prefs')
    if (raw) {
      try { return JSON.parse(raw) } catch {}
    }
    return { diet: ['Vegetarian'], cuisine: ['Italian','Thai'], entertainment: ['Live Music'], activities: ['Hiking'], preferredTimes: ['Any Time'], shareWithPartner: false, zip: '', searchDistance: 25, starRating: 3, costLevel: 2 }
  })
  const [draft, setDraft] = useState<Prefs>(prefs)
  const [justSaved, setJustSaved] = useState(false)

  useEffect(() => { setDraft(prefs) }, [isEditing])

  const toggle = (key: ArrayPrefKey, value: string) => {
    setDraft(prev => {
      const current = prev[key]
      const exists = current.includes(value)
      const nextValues = exists ? (current as string[]).filter((v: string) => v !== value) : [...current, value]
      return { ...prev, [key]: nextValues }
    })
  }

  const save = async () => {
    setPrefs(draft)
    safe.setItem('ds_prefs', JSON.stringify(draft))
    try {
      await Api.preferences.save({
        zip: draft.zip,
        preferences: {
          dietRestrictions: draft.diet,
          cuisines: draft.cuisine,
          entertainment: draft.entertainment,
          activities: draft.activities,
          preferredTimes: draft.preferredTimes,
          shareWithPartner: draft.shareWithPartner,
          searchDistance: draft.searchDistance,
          starRating: draft.starRating,
          costLevel: draft.costLevel
        }
      })
    } catch (e) {
      console.error('Failed to save preferences:', e)
    }
    setIsEditing(false)
    setJustSaved(true)
    setTimeout(() => setJustSaved(false), 1200)
  }

  const Section = useMemo(() => function Section({ title, options, keyName }: { title: string; options: string[]; keyName: ArrayPrefKey }) {
    const selected: string[] = (isEditing ? draft[keyName] : prefs[keyName]) as string[] || []
    const [showAll, setShowAll] = useState(false)
    
    // Show first 11 items by default, or all if showAll is true
    const displayOptions = isEditing ? (showAll ? options : options.slice(0, 11)) : (selected || [])
    const hasMore = isEditing && options.length > 11
    
    return (
      <section>
        <h3 className="text-sm font-semibold text-black/90">{title}</h3>
        <div className="mt-2 flex flex-wrap gap-2">
          {displayOptions.map(option => {
            const isSelected = selected.includes(option)
            const className = `chip ${isSelected ? 'chip-selected' : ''}`
            return (
              <button
                key={option}
                type="button"
                className={className}
                onClick={() => isEditing && toggle(keyName, option)}
                aria-pressed={isSelected}
              >
                {option}
              </button>
            )
          })}
          {hasMore && !showAll && (
            <button
              type="button"
              className="chip bg-gray-100 text-gray-600 hover:bg-gray-200"
              onClick={() => setShowAll(true)}
            >
              Show More ({options.length - 11} more)
            </button>
          )}
          {hasMore && showAll && (
            <button
              type="button"
              className="chip bg-gray-100 text-gray-600 hover:bg-gray-200"
              onClick={() => setShowAll(false)}
            >
              Show Less
            </button>
          )}
        </div>
      </section>
    )
  }, [isEditing, draft, prefs])

  return (
    <div id="preferences-card" className="glass-card p-4">
      <div className="flex items-center justify-between relative">
        <h2 className="text-xl font-semibold text-black">Preferences</h2>
        {!isEditing ? (
          <button className="btn-nav px-3 py-1" aria-label="Edit Preferences" onClick={() => setIsEditing(true)}>Edit</button>
        ) : (
          <div className="flex items-center gap-2">
            <button className="btn-primary px-4 py-2" onClick={save}>Save</button>
            <button className="btn-nav px-3 py-2" onClick={() => { setDraft(prefs); setIsEditing(false) }}>Cancel</button>
          </div>
        )}
        {justSaved && (
          <span className="absolute right-0 -top-6 text-xs bg-white/90 border border-black/10 rounded-full px-3 py-1 shadow">
            Saved
          </span>
        )}
      </div>

      <div className="mt-3 space-y-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="text-sm font-semibold text-black/90" htmlFor="zip">Zipcode</label>
          {!isEditing ? (
            <div className="chip select-none">{prefs.zip || 'Not set'}</div>
          ) : (
            <input id="zip" inputMode="numeric" pattern="\\d*" maxLength={10} className="w-full sm:w-40 border p-2 rounded-xl" placeholder="e.g. 90210" value={draft.zip}
              onChange={e => setDraft(prev => ({ ...prev, zip: e.target.value }))} />
          )}
          <label className="flex items-center gap-2 ml-auto text-sm">
            <input type="checkbox" checked={isEditing ? draft.shareWithPartner : prefs.shareWithPartner} disabled={!isEditing}
              onChange={e => setDraft(prev => ({ ...prev, shareWithPartner: e.target.checked }))} />
            <span>Share with partner</span>
          </label>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="text-sm font-semibold text-black/90" htmlFor="distance">Search Distance</label>
          {!isEditing ? (
            <div className="chip select-none">{prefs.searchDistance} miles</div>
          ) : (
            <select 
              id="distance"
              value={draft.searchDistance} 
              onChange={(e) => setDraft(prev => ({ ...prev, searchDistance: Number(e.target.value) }))}
              className="border border-black/20 rounded-lg px-3 py-2 text-sm bg-white w-full sm:w-32"
            >
              <option value={5}>5 miles</option>
              <option value={10}>10 miles</option>
              <option value={25}>25 miles</option>
              <option value={50}>50 miles</option>
            </select>
          )}
        </div>

        {/* Star Rating Selection */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="text-sm font-semibold text-black/90">Star Rating Preference</label>
          {!isEditing ? (
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map(star => (
                <span key={star} className={`text-lg ${star <= prefs.starRating ? 'text-yellow-400' : 'text-gray-300'}`}>
                  {star <= prefs.starRating ? '★' : '☆'}
                </span>
              ))}
              <span className="ml-2 text-sm text-black/70">({prefs.starRating} stars)</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map(star => (
                <button
                  key={star}
                  type="button"
                  onClick={() => setDraft(prev => ({ ...prev, starRating: star }))}
                  className={`text-2xl transition-colors ${
                    star <= draft.starRating ? 'text-yellow-400' : 'text-gray-300'
                  } hover:text-yellow-500`}
                >
                  {star <= draft.starRating ? '★' : '☆'}
                </button>
              ))}
              <span className="ml-2 text-sm text-black/70">({draft.starRating} stars)</span>
            </div>
          )}
        </div>

        {/* Cost Level Selection */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <label className="text-sm font-semibold text-black/90">Cost Level Preference</label>
          {!isEditing ? (
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4].map(dollar => (
                <span key={dollar} className={`text-lg font-bold ${dollar <= prefs.costLevel ? 'text-green-500' : 'text-gray-300'}`}>
                  {dollar <= prefs.costLevel ? '$' : '$'}
                </span>
              ))}
              <span className="ml-2 text-sm text-black/70">({prefs.costLevel} dollars)</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4].map(dollar => (
                <button
                  key={dollar}
                  type="button"
                  onClick={() => setDraft(prev => ({ ...prev, costLevel: dollar }))}
                  className={`text-2xl font-bold transition-colors ${
                    dollar <= draft.costLevel ? 'text-green-500' : 'text-gray-300'
                  } hover:text-green-600`}
                >
                  {dollar <= draft.costLevel ? '$' : '$'}
                </button>
              ))}
              <span className="ml-2 text-sm text-black/70">({draft.costLevel} dollars)</span>
            </div>
          )}
        </div>

        <Section title="Preferred Times" options={TIME_OPTIONS} keyName="preferredTimes" />
        <Section title="Diet" options={DIET_OPTIONS} keyName="diet" />
        <Section title="Cuisine" options={CUISINE_OPTIONS} keyName="cuisine" />
        <Section title="Entertainment" options={ENTERTAINMENT_OPTIONS} keyName="entertainment" />
        <Section title="Activities" options={ACTIVITIES_OPTIONS} keyName="activities" />
      </div>
    </div>
  )
}


